import { useCallback, useEffect, useRef, useState } from 'react';
import { testAnswerSchema, type TestAnswer, type TestSectionView } from '@nest-recruit/shared';
import { ApiError } from '../../../lib/api-client';
import { saveTestAnswer } from '../api/test-player-api';

/** How long after the last keystroke a typed answer is sent. */
const TYPING_PAUSE_MS = 800;
/** A save that failed on the network is tried again after this, and on reconnecting. */
const RETRY_MS = 4000;
const STORE_PREFIX = 'nr-test:';

export type SaveState = 'saved' | 'saving' | 'waiting' | 'refused';

export interface TestAnswers {
  readonly answers: ReadonlyMap<string, TestAnswer | null>;
  readonly states: ReadonlyMap<string, SaveState>;
  /** Why the server refused the last save of a question, in its words. */
  readonly refusals: ReadonlyMap<string, string>;
  readonly change: (questionId: string, answer: TestAnswer, isTyping: boolean) => void;
  /** Sends everything not yet saved, and says whether it all landed. */
  readonly flush: () => Promise<boolean>;
  readonly unsavedCount: number;
}

/**
 * Autosave for ASM-03 (F9.04 "autosave per answer; resume after disconnect"). A choice
 * goes at once; typing goes after a pause and on leaving the field. Saves of one
 * question go in order, so an older save can never land after a newer one. Anything
 * not yet saved is also kept in this browser, so a closed tab or a dead battery loses
 * nothing typed — it is sent when the link is opened again.
 */
export function useTestAnswers(token: string, section: TestSectionView | null): TestAnswers {
  const [answers, setAnswers] = useState<ReadonlyMap<string, TestAnswer | null>>(new Map());
  const [states, setStates] = useState<ReadonlyMap<string, SaveState>>(new Map());
  const [refusals, setRefusals] = useState<ReadonlyMap<string, string>>(new Map());
  const pending = useRef(new Map<string, TestAnswer>());
  const timers = useRef(new Map<string, number>());
  const chains = useRef(new Map<string, Promise<boolean>>());
  /** The retry timer calls the current `send` without `saveNow` depending on it. */
  const sendRef = useRef<((questionId: string) => Promise<boolean>) | null>(null);
  const [unsavedCount, setUnsavedCount] = useState(0);

  const setState = useCallback((questionId: string, state: SaveState) => {
    setStates((current) => new Map(current).set(questionId, state));
  }, []);

  const saveNow = useCallback(
    async (questionId: string): Promise<boolean> => {
      const answer = pending.current.get(questionId);
      if (!answer) return true;
      setState(questionId, 'saving');
      try {
        await saveTestAnswer(token, questionId, answer);
        // A newer answer typed while this one was in flight is still pending.
        if (pending.current.get(questionId) === answer) {
          pending.current.delete(questionId);
          forget(token, questionId);
          setUnsavedCount(pending.current.size);
          setState(questionId, 'saved');
        }
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          // Refused, not lost: time is up, or the link has closed. Retrying cannot help.
          pending.current.delete(questionId);
          forget(token, questionId);
          setUnsavedCount(pending.current.size);
          setRefusals((current) => new Map(current).set(questionId, error.message));
          setState(questionId, 'refused');
          return false;
        }
        setState(questionId, 'waiting');
        window.clearTimeout(timers.current.get(questionId));
        timers.current.set(questionId, window.setTimeout(() => void sendRef.current?.(questionId), RETRY_MS));
        return false;
      }
    },
    [setState, token],
  );

  /** One save at a time per question, in the order they were made. */
  const send = useCallback(
    (questionId: string): Promise<boolean> => {
      const previous = chains.current.get(questionId) ?? Promise.resolve(true);
      const next = previous.then(() => saveNow(questionId));
      chains.current.set(questionId, next);
      return next;
    },
    [saveNow],
  );
  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  // A new section starts from the server's answers, plus anything this browser still
  // holds. Only a different section resets the page — a refetch of the same one, which
  // happens whenever the tab regains focus, must not throw away what is being typed.
  const appliedKey = useRef('');
  const sectionKey = section ? `${section.number}:${section.questions.map((question) => question.id).join(',')}` : '';
  useEffect(() => {
    if (!section || appliedKey.current === sectionKey) return;
    appliedKey.current = sectionKey;
    const stored = readStored(token);
    const next = new Map<string, TestAnswer | null>();
    for (const question of section.questions) {
      const kept = stored.get(question.id);
      next.set(question.id, kept ?? question.answer);
      if (kept) pending.current.set(question.id, kept);
    }
    setAnswers(next);
    setUnsavedCount(pending.current.size);
    for (const questionId of pending.current.keys()) void send(questionId);
  }, [section, sectionKey, send, token]);

  useEffect(() => {
    const onOnline = (): void => {
      for (const questionId of pending.current.keys()) void send(questionId);
    };
    window.addEventListener('online', onOnline);
    const owned = timers.current;
    return () => {
      window.removeEventListener('online', onOnline);
      for (const timer of owned.values()) window.clearTimeout(timer);
    };
  }, [send]);

  const change = useCallback(
    (questionId: string, answer: TestAnswer, isTyping: boolean) => {
      setAnswers((current) => new Map(current).set(questionId, answer));
      setRefusals((current) => {
        if (!current.has(questionId)) return current;
        const next = new Map(current);
        next.delete(questionId);
        return next;
      });
      pending.current.set(questionId, answer);
      store(token, questionId, answer);
      setUnsavedCount(pending.current.size);
      window.clearTimeout(timers.current.get(questionId));
      if (isTyping) {
        setState(questionId, 'waiting');
        timers.current.set(questionId, window.setTimeout(() => void send(questionId), TYPING_PAUSE_MS));
        return;
      }
      void send(questionId);
    },
    [send, setState, token],
  );

  const flush = useCallback(async (): Promise<boolean> => {
    for (const timer of timers.current.values()) window.clearTimeout(timer);
    timers.current.clear();
    const results = await Promise.all([...pending.current.keys()].map((questionId) => send(questionId)));
    return results.every(Boolean);
  }, [send]);

  return { answers, states, refusals, change, flush, unsavedCount };
}

// --- This browser's copy of what is not yet saved -----------------------------------------

function keyOf(token: string): string {
  return `${STORE_PREFIX}${token.slice(0, 24)}`;
}

function readStored(token: string): ReadonlyMap<string, TestAnswer> {
  try {
    const raw = window.localStorage.getItem(keyOf(token));
    if (!raw) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return new Map();
    const entries = Object.entries(parsed).flatMap(([questionId, value]) => {
      const answer = testAnswerSchema.safeParse(value);
      return answer.success ? [[questionId, answer.data] as const] : [];
    });
    return new Map(entries);
  } catch {
    // Storage switched off or full: autosave to the server still works.
    return new Map();
  }
}

function store(token: string, questionId: string, answer: TestAnswer): void {
  try {
    const current = Object.fromEntries(readStored(token));
    window.localStorage.setItem(keyOf(token), JSON.stringify({ ...current, [questionId]: answer }));
  } catch {
    // Storage switched off or full: autosave to the server still works.
  }
}

function forget(token: string, questionId: string): void {
  try {
    const current = Object.fromEntries(readStored(token));
    delete current[questionId];
    if (Object.keys(current).length === 0) window.localStorage.removeItem(keyOf(token));
    else window.localStorage.setItem(keyOf(token), JSON.stringify(current));
  } catch {
    // Storage switched off: nothing was kept, so there is nothing to forget.
  }
}
